import { defineStore } from 'pinia';
import { now, nowMs } from 'common/utils/date.utils';

export type TrialUpsellTrigger =
  | 'login_5'
  | 'time_48h'
  | 'high_intent_first_value'
  | 'high_intent_report'
  | 'high_intent_second_class'
  | 'high_intent_second_teacher'
  | 'high_intent_multiple_students'
  | 'manual_banner';

export interface TrialUpsellTrackingContext {
  can_show_today: boolean;
  cooldown_until_ms: number | null;
  high_intent_cooldown_until_ms: number | null;
  last_dismissed_at_ms: number | null;
  last_shown_at_ms: number | null;
  last_trigger: TrialUpsellTrigger | null;
  shown_this_session: boolean;
  login_count_since_reset: number;
  competence_observations_created_since_reset: number;
  reports_created_since_reset: number;
  classes_created_since_reset: number;
  teacher_invites_created_since_reset: number;
  students_created_or_linked_since_reset: number;
  first_value_success_since_reset: boolean;
  post_entry_growth_upsell_bypass_armed: boolean;
}

interface ITrialUpsellState {
  cycleStartedAtMs: number;
  loginCountSinceReset: number;
  competenceObservationsCreatedSinceReset: number;
  reportsCreatedSinceReset: number;
  classesCreatedSinceReset: number;
  teacherInvitesCreatedSinceReset: number;
  studentsCreatedOrLinkedSinceReset: number;
  firstValueSuccessSinceReset: boolean;
  eligibilityCheckToken: number;
  dailyQuotaDate: string | null;
  dailyQuotaShowCount: number;
  lastShownAtMs: number | null;
  lastDismissedAtMs: number | null;
  shownThisSession: boolean;
  lastTrigger: TrialUpsellTrigger | null;
  postEntryGrowthUpsellBypassArmed: boolean;
}

const HOURS_24_MS = 60 * 60 * 24 * 1000;
const HOURS_48_MS = 60 * 60 * 48 * 1000;
const LOGIN_THRESHOLD = 5;
const COMPETENCE_OBSERVATION_THRESHOLD = 15;
const REPORT_THRESHOLD = 1;
const CLASS_THRESHOLD = 2;
const TEACHER_INVITE_THRESHOLD = 2;
const STUDENT_CREATED_OR_LINKED_THRESHOLD = 2;
const MAX_SHOWS_PER_DAY = 2;

const getTodayDateKey = (): string => {
  const currentDate = now();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const day = String(currentDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLastInteractionAtMs = (
  state: Pick<ITrialUpsellState, 'lastDismissedAtMs' | 'lastShownAtMs'>,
): number | null => {
  const timestamps = [state.lastShownAtMs, state.lastDismissedAtMs].filter((value): value is number => value != null);
  if (!timestamps.length) return null;
  return Math.max(...timestamps);
};

const capCounter = (value: number | undefined, max: number): number => {
  return Math.min(value ?? 0, max);
};

export const useTrialUpsellStore = defineStore('trialUpsell', {
  state: (): ITrialUpsellState => ({
    cycleStartedAtMs: nowMs(),
    loginCountSinceReset: 0,
    competenceObservationsCreatedSinceReset: 0,
    reportsCreatedSinceReset: 0,
    classesCreatedSinceReset: 0,
    teacherInvitesCreatedSinceReset: 0,
    studentsCreatedOrLinkedSinceReset: 0,
    firstValueSuccessSinceReset: false,
    eligibilityCheckToken: 0,
    dailyQuotaDate: null,
    dailyQuotaShowCount: 0,
    lastShownAtMs: null,
    lastDismissedAtMs: null,
    shownThisSession: false,
    lastTrigger: null,
    postEntryGrowthUpsellBypassArmed: false,
  }),
  getters: {
    isLoginTriggerMet: (state): boolean => {
      return capCounter(state.loginCountSinceReset, LOGIN_THRESHOLD) >= LOGIN_THRESHOLD;
    },
    isTimeTriggerMet: (state): boolean => {
      void state.loginCountSinceReset;
      void state.eligibilityCheckToken;
      const currentTimeMs = nowMs();
      const baselineMs = getLastInteractionAtMs(state) ?? state.cycleStartedAtMs;
      return currentTimeMs - baselineMs >= HOURS_48_MS;
    },
    isGeneralCooldownMet: (state): boolean => {
      void state.loginCountSinceReset;
      void state.eligibilityCheckToken;
      void state.classesCreatedSinceReset;
      void state.competenceObservationsCreatedSinceReset;
      void state.reportsCreatedSinceReset;
      void state.teacherInvitesCreatedSinceReset;
      void state.studentsCreatedOrLinkedSinceReset;
      void state.firstValueSuccessSinceReset;
      const lastInteractionAtMs = getLastInteractionAtMs(state);
      if (lastInteractionAtMs == null) return true;
      return nowMs() - lastInteractionAtMs >= HOURS_24_MS;
    },
    isHighIntentCooldownMet: (state): boolean => {
      void state.eligibilityCheckToken;
      void state.classesCreatedSinceReset;
      void state.competenceObservationsCreatedSinceReset;
      void state.reportsCreatedSinceReset;
      void state.teacherInvitesCreatedSinceReset;
      void state.studentsCreatedOrLinkedSinceReset;
      void state.firstValueSuccessSinceReset;
      const lastInteractionAtMs = getLastInteractionAtMs(state);
      if (lastInteractionAtMs == null) return true;
      return nowMs() - lastInteractionAtMs >= HOURS_24_MS;
    },
    hasProductValueSignal: (state): boolean => {
      return (
        !!state.firstValueSuccessSinceReset ||
        state.classesCreatedSinceReset > 0 ||
        state.competenceObservationsCreatedSinceReset > 0 ||
        state.reportsCreatedSinceReset > 0 ||
        state.teacherInvitesCreatedSinceReset > 0 ||
        state.studentsCreatedOrLinkedSinceReset > 0
      );
    },
    isFirstValueHighIntentMet: (state): boolean => {
      return (
        !!state.firstValueSuccessSinceReset ||
        (state.classesCreatedSinceReset >= 1 && state.competenceObservationsCreatedSinceReset > 0)
      );
    },
    resolvedHighIntentTrigger(): TrialUpsellTrigger | null {
      if (this.isFirstValueHighIntentMet) return 'high_intent_first_value';
      if (this.reportsCreatedSinceReset >= REPORT_THRESHOLD) return 'high_intent_report';
      if (this.classesCreatedSinceReset >= CLASS_THRESHOLD) return 'high_intent_second_class';
      if (this.teacherInvitesCreatedSinceReset >= TEACHER_INVITE_THRESHOLD) return 'high_intent_second_teacher';
      if (this.studentsCreatedOrLinkedSinceReset >= STUDENT_CREATED_OR_LINKED_THRESHOLD) {
        return 'high_intent_multiple_students';
      }
      return null;
    },
    isPreconditionMet(): boolean {
      return this.isLoginTriggerMet || this.isTimeTriggerMet;
    },
    isActivityThresholdMet(): boolean {
      return this.hasProductValueSignal;
    },
    resolvedTrigger(): TrialUpsellTrigger | null {
      if (this.shownThisSession) return null;
      if (!this.canShowToday) return null;
      if (!this.isGeneralCooldownMet) return null;

      const highIntentTrigger = this.resolvedHighIntentTrigger;
      if (highIntentTrigger && this.isHighIntentCooldownMet) return highIntentTrigger;
      if (this.isLoginTriggerMet) return 'login_5';
      if (this.isTimeTriggerMet) return 'time_48h';
      return null;
    },
    shouldShow(): boolean {
      return this.resolvedTrigger != null;
    },
    canBypassActiveGrowthPopup(): boolean {
      return this.postEntryGrowthUpsellBypassArmed && this.resolvedTrigger === 'high_intent_first_value';
    },
    showsToday: (state): number => {
      return state.dailyQuotaDate === getTodayDateKey() ? state.dailyQuotaShowCount : 0;
    },
    canShowToday(): boolean {
      return this.showsToday < MAX_SHOWS_PER_DAY;
    },
    triggerContext(): TrialUpsellTrackingContext {
      const lastInteractionAtMs = getLastInteractionAtMs(this);
      const cooldownUntilMs = lastInteractionAtMs == null ? null : lastInteractionAtMs + HOURS_24_MS;
      const highIntentCooldownUntilMs = lastInteractionAtMs == null ? null : lastInteractionAtMs + HOURS_24_MS;

      return {
        can_show_today: this.canShowToday,
        cooldown_until_ms: cooldownUntilMs,
        high_intent_cooldown_until_ms: highIntentCooldownUntilMs,
        last_dismissed_at_ms: this.lastDismissedAtMs ?? null,
        last_shown_at_ms: this.lastShownAtMs ?? null,
        last_trigger: this.lastTrigger ?? null,
        shown_this_session: !!this.shownThisSession,
        login_count_since_reset: capCounter(this.loginCountSinceReset, LOGIN_THRESHOLD),
        competence_observations_created_since_reset: capCounter(
          this.competenceObservationsCreatedSinceReset,
          COMPETENCE_OBSERVATION_THRESHOLD,
        ),
        reports_created_since_reset: capCounter(this.reportsCreatedSinceReset, REPORT_THRESHOLD),
        classes_created_since_reset: capCounter(this.classesCreatedSinceReset, CLASS_THRESHOLD),
        teacher_invites_created_since_reset: capCounter(this.teacherInvitesCreatedSinceReset, TEACHER_INVITE_THRESHOLD),
        students_created_or_linked_since_reset: capCounter(
          this.studentsCreatedOrLinkedSinceReset,
          STUDENT_CREATED_OR_LINKED_THRESHOLD,
        ),
        first_value_success_since_reset: !!this.firstValueSuccessSinceReset,
        post_entry_growth_upsell_bypass_armed: !!this.postEntryGrowthUpsellBypassArmed,
      };
    },
  },
  actions: {
    recordLogin(): void {
      this.shownThisSession = false;
      this.loginCountSinceReset = Math.min(this.loginCountSinceReset + 1, LOGIN_THRESHOLD);
    },
    recordCompetenceObservationsCreated(amount = 1): void {
      if (amount <= 0) return;
      this.competenceObservationsCreatedSinceReset = Math.min(
        this.competenceObservationsCreatedSinceReset + amount,
        COMPETENCE_OBSERVATION_THRESHOLD,
      );
    },
    recordReportsCreated(amount = 1): void {
      if (amount <= 0) return;
      this.reportsCreatedSinceReset = Math.min(this.reportsCreatedSinceReset + amount, REPORT_THRESHOLD);
    },
    recordClassesCreated(amount = 1): void {
      if (amount <= 0) return;
      this.classesCreatedSinceReset = Math.min(this.classesCreatedSinceReset + amount, CLASS_THRESHOLD);
    },
    recordTeacherInvitesCreated(amount = 1): void {
      if (amount <= 0) return;
      this.teacherInvitesCreatedSinceReset = Math.min(
        this.teacherInvitesCreatedSinceReset + amount,
        TEACHER_INVITE_THRESHOLD,
      );
    },
    recordStudentsCreatedOrLinked(amount = 1): void {
      if (amount <= 0) return;
      this.studentsCreatedOrLinkedSinceReset = Math.min(
        this.studentsCreatedOrLinkedSinceReset + amount,
        STUDENT_CREATED_OR_LINKED_THRESHOLD,
      );
    },
    recordFirstValueSuccess(): void {
      this.firstValueSuccessSinceReset = true;
    },
    armPostEntryGrowthUpsellBypass(): void {
      this.postEntryGrowthUpsellBypassArmed = true;
    },
    requestEligibilityCheck(): void {
      if (this.eligibilityCheckToken >= Number.MAX_SAFE_INTEGER) {
        this.eligibilityCheckToken = 0;
        return;
      }
      this.eligibilityCheckToken += 1;
    },
    markShown(trigger?: TrialUpsellTrigger | null): void {
      const resolvedTrigger = trigger ?? this.resolvedTrigger;
      const todayDateKey = getTodayDateKey();
      if (this.dailyQuotaDate !== todayDateKey) {
        this.dailyQuotaDate = todayDateKey;
        this.dailyQuotaShowCount = 0;
      }
      this.dailyQuotaShowCount = Math.min(this.dailyQuotaShowCount + 1, MAX_SHOWS_PER_DAY);
      this.lastShownAtMs = nowMs();
      this.shownThisSession = true;
      this.lastTrigger = resolvedTrigger;
      this.postEntryGrowthUpsellBypassArmed = false;
      this.eligibilityCheckToken = 0;
    },
    markManualBannerOpen(): void {
      this.lastShownAtMs = nowMs();
      this.shownThisSession = true;
      this.lastTrigger = 'manual_banner';
      this.postEntryGrowthUpsellBypassArmed = false;
    },
    markDismissed(): void {
      this.lastDismissedAtMs = nowMs();
      this.resetCycle();
    },
    tryRegisterShow(): boolean {
      if (!this.canShowToday) return false;
      this.markShown();
      return true;
    },
    resetCycle(): void {
      this.cycleStartedAtMs = nowMs();
      this.loginCountSinceReset = 0;
      this.competenceObservationsCreatedSinceReset = 0;
      this.reportsCreatedSinceReset = 0;
      this.classesCreatedSinceReset = 0;
      this.teacherInvitesCreatedSinceReset = 0;
      this.studentsCreatedOrLinkedSinceReset = 0;
      this.firstValueSuccessSinceReset = false;
      this.postEntryGrowthUpsellBypassArmed = false;
      this.eligibilityCheckToken = 0;
    },
  },
  persist: true,
});
